import { type ReactNode, useEffect, useRef, useState } from 'react';
import Text from '@/components/UI/Text';
import Box from '@/components/Unistyles/Box';
import TextInput from '@/components/Unistyles/TextInput';
import useCountdown from '@/hooks/useCountdown';
import FooterActionButton from './FooterActionButton';
import ModalKeyboardAwareScrollView from './ModalKeyboardAwareScrollView';
import ModalKeyboardStickyView from './ModalKeyboardStickyView';

export type DestructiveActionResult = { success: boolean };

type ActionState = 'ready' | 'submitting' | 'failed' | 'succeeded';

export function DestructiveActionHeading({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <Text
      center
      accessibilityRole="header"
      size="xl"
      fontWeight="semibold"
      lineHeight={28}
    >
      {children}
    </Text>
  );
}

export function DestructiveActionDescription({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <Text center size="md" lineHeight="2xl" variant="typographySecondary">
      {children}
    </Text>
  );
}

export default function DestructiveAction({
  children,
  confirmationName,
  label,
  submittingLabel,
  onAction,
  onSuccess,
  testID = 'DestructiveActionButton',
}: {
  children: ReactNode;
  confirmationName?: string;
  label: string;
  submittingLabel: string;
  onAction: () => Promise<DestructiveActionResult>;
  onSuccess: () => void;
  testID?: string;
}) {
  const [confirmation, setConfirmation] = useState('');
  const { remainingMs: countdownMs, hasElapsed } = useCountdown(
    confirmationName === undefined ? undefined : 0,
  );
  const [state, setState] = useState<ActionState>('ready');
  const inFlight = useRef(false);
  const completed = useRef(false);
  const confirmed =
    confirmationName === undefined ||
    (confirmationName.trim().length > 0 &&
      confirmation.trim() === confirmationName.trim());
  const submitting = state === 'submitting';
  const locked = submitting || state === 'succeeded';

  useEffect(() => {
    if (state === 'succeeded' && !completed.current) {
      completed.current = true;
      onSuccess();
    }
  }, [state, onSuccess]);

  const submit = async () => {
    if (
      !hasElapsed() ||
      !confirmed ||
      inFlight.current ||
      state === 'succeeded'
    )
      return;
    inFlight.current = true;
    setState('submitting');
    try {
      const result = await onAction();
      if (result.success) {
        setState('succeeded');
      } else {
        inFlight.current = false;
        setState('failed');
      }
    } catch {
      inFlight.current = false;
      setState('failed');
    }
  };

  return (
    <Box flex>
      <ModalKeyboardAwareScrollView
        contentContainerStyle={{ flexGrow: 1 }}
        bottomOffset={200}
      >
        <Box
          flexGrow={1}
          justifyContent="center"
          width="100%"
          maxWidth={560}
          alignSelf="center"
          px="xl"
          py="2xl"
          gap="2xl"
        >
          <Box gap="md">{children}</Box>
          {confirmationName !== undefined && (
            <Box gap="md">
              <Text center size="md" lineHeight="2xl">
                Type{' '}
                <Text size="md" fontWeight="semibold">
                  {confirmationName}
                </Text>{' '}
                to confirm.
              </Text>
              <TextInput
                accessibilityLabel="Confirmation name"
                testID="DestructiveActionConfirmationInput"
                value={confirmation}
                onChangeText={setConfirmation}
                editable={!locked}
                autoCorrect={false}
                autoCapitalize="none"
              />
            </Box>
          )}
          {state === 'failed' && (
            <Text
              center
              accessibilityRole="alert"
              size="md"
              lineHeight="2xl"
              variant="error"
            >
              Could not complete this action. Please try again.
            </Text>
          )}
        </Box>
      </ModalKeyboardAwareScrollView>
      <ModalKeyboardStickyView>
        <FooterActionButton
          testID={testID}
          footerLayout="trailing"
          label={label}
          countdownMs={countdownMs}
          loadingLabel={submittingLabel}
          loading={submitting}
          disabled={!confirmed || locked}
          variant="negative"
          onPress={submit}
        />
      </ModalKeyboardStickyView>
    </Box>
  );
}
